import { Button } from '@agentic-flow-studio/shared/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@agentic-flow-studio/shared/ui/card';
import { Checkbox } from '@agentic-flow-studio/shared/ui/checkbox';
import { Input } from '@agentic-flow-studio/shared/ui/input';
import { Label } from '@agentic-flow-studio/shared/ui/label';
import { Switch } from '@agentic-flow-studio/shared/ui/switch';
import { AlertCircle, Sparkles } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { trpc } from '@/utils/trpc';

/** `<input type="date">` value → epoch seconds at the start of that local day. */
function startOfDay(value: string): number {
  return Math.floor(new Date(`${value}T00:00:00`).getTime() / 1000);
}

/** `<input type="date">` value → epoch seconds at the end of that local day, inclusive. */
function endOfDay(value: string): number {
  return Math.floor(new Date(`${value}T23:59:59`).getTime() / 1000);
}

function isoDaysAgo(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

/**
 * Analyse mail history on purpose.
 *
 * Exists because enabling analysis is deliberately never retroactive — mail that
 * predates the workspace's watermark is delivered but never summarized, and this is
 * the only thing that reclaims it. The design constraint from ADR-0003 is that the
 * cost is stated *before* anything runs: a backfill that cannot be costed in
 * advance is a trap for exactly the customer who limited analysis to control spend.
 */
export function AnalysisSettings({ isReadOnly }: { isReadOnly: boolean }) {
  const [from, setFrom] = useState(() => isoDaysAgo(30));
  const [to, setTo] = useState(() => isoDaysAgo(0));
  const [includeOutdated, setIncludeOutdated] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [isRunning, setIsRunning] = useState(false);

  const utils = trpc.useUtils();
  const range = { from: startOfDay(from), to: endOfDay(to) };
  const rangeIsValid = Boolean(from) && Boolean(to) && range.from <= range.to;

  const policyQuery = trpc.analysis.getPolicy.useQuery();
  const analysisEnabled = policyQuery.data?.analysisEnabled ?? true;

  const setEnabled = trpc.analysis.setEnabled.useMutation({
    onSuccess: (policy) => {
      toast.success(policy.analysisEnabled ? 'AI analysis turned on' : 'AI analysis turned off');
      utils.analysis.invalidate();
    },
    onError: (err) => toast.error(`Could not update setting: ${err.message}`),
  });

  const previewQuery = trpc.analysis.previewBackfill.useQuery(
    { range },
    { enabled: rangeIsValid && analysisEnabled, staleTime: 10_000 }
  );
  const runBatch = trpc.analysis.runBackfillBatch.useMutation();

  const preview = previewQuery.data;
  const selected = preview
    ? preview.neverAnalyzed + (includeOutdated ? preview.outdatedVersion : 0)
    : 0;
  const remainingAllowance = preview ? Math.max(preview.limitCount - preview.actionCount, 0) : 0;

  /**
   * Drives the batch loop client-side. Each call analyses a bounded batch and
   * reports what is left, so progress is real rather than a spinner — and closing
   * the tab stops the spend rather than leaving a job grinding through quota.
   */
  const run = async () => {
    setIsRunning(true);
    setProgress({ done: 0, total: selected });
    let done = 0;

    try {
      for (;;) {
        const result = await runBatch.mutateAsync({ range, includeOutdated });
        done += result.analyzed;
        setProgress({ done, total: Math.max(selected, done + result.remaining) });

        if (!result.hadQuota) {
          toast.error('Ran out of usage allowance. Analysed what was possible.');
          break;
        }
        if (result.remaining === 0) {
          toast.success(`Analysed ${done} thread${done === 1 ? '' : 's'}.`);
          break;
        }
        // A batch that analysed nothing and failed nothing would loop forever.
        if (result.analyzed === 0 && result.failed === 0) break;
      }
    } catch (err) {
      toast.error(`Backfill failed: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setIsRunning(false);
      setProgress(null);
      utils.analysis.previewBackfill.invalidate();
      utils.invalidate();
    }
  };

  return (
    <div className="space-y-8 max-w-2xl">
      <Card className="rounded-panel border-border bg-card">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            <CardTitle>AI analysis</CardTitle>
          </div>
          <CardDescription>
            Summaries, priorities and categories are produced by sending thread content to an AI
            model.
          </CardDescription>
        </CardHeader>

        <CardContent>
          <label className="flex items-start justify-between gap-6">
            <span className="space-y-1">
              <span className="block font-medium">Analyse incoming mail</span>
              {/* Says plainly what stays working, because the fear this setting has to
 answer is "will turning this off break my inbox". It will not — mail
 still arrives, only the summary is withheld. */}
              <span className="block text-sm text-muted-foreground">
                When off, mail still arrives with its subject, sender and labels, and rules that do
                not depend on AI keep running. Only the summaries stop.
              </span>
            </span>
            <Switch
              checked={analysisEnabled}
              disabled={isReadOnly || setEnabled.isPending || policyQuery.isLoading}
              onCheckedChange={(checked) => setEnabled.mutate({ enabled: checked })}
            />
          </label>

          {analysisEnabled && policyQuery.data?.analysisStartAt && (
            <p className="mt-4 text-sm text-muted-foreground">
              Analysing mail received since{' '}
              {new Date(policyQuery.data.analysisStartAt * 1000).toLocaleDateString()}. Older mail
              is delivered but not summarized — use the backfill below to include it.
            </p>
          )}
        </CardContent>
      </Card>

      {!analysisEnabled ? null : (
        <Card className="rounded-panel border-border bg-card">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              <CardTitle>Analyse mail history</CardTitle>
            </div>
            <CardDescription>
              New mail is summarized as it arrives. Older mail is delivered but left un-summarized
              until you ask for it here.
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-5">
            <div className="flex flex-wrap gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="backfill-from">From</Label>
                <Input
                  id="backfill-from"
                  type="date"
                  value={from}
                  max={to}
                  disabled={isReadOnly || isRunning}
                  onChange={(e) => setFrom(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="backfill-to">To</Label>
                <Input
                  id="backfill-to"
                  type="date"
                  value={to}
                  min={from}
                  disabled={isReadOnly || isRunning}
                  onChange={(e) => setTo(e.target.value)}
                />
              </div>
            </div>

            {!rangeIsValid && (
              <p className="text-sm text-destructive">
                Pick a start date on or before the end date.
              </p>
            )}

            {rangeIsValid && previewQuery.isLoading && (
              <p className="text-sm text-muted-foreground">Counting threads…</p>
            )}

            {preview && (
              <div className="rounded-lg border border-border/60 bg-muted/30 p-4 space-y-3 text-sm">
                <label className="flex items-start gap-2.5">
                  <Checkbox checked disabled className="mt-0.5" />
                  <span>
                    <span className="font-medium tabular-nums">
                      {preview.neverAnalyzed.toLocaleString()}
                    </span>{' '}
                    thread{preview.neverAnalyzed === 1 ? '' : 's'} never analysed
                  </span>
                </label>

                {/*
 Re-analysing already-summarized threads is never a default and is
 never offered as a bare toggle. Content changes re-analyse themselves
 for free, so the only honest reason to spend on it in bulk is that the
 prompt or model version moved — which is what makes it countable.
 */}
                <label className="flex items-start gap-2.5">
                  <Checkbox
                    checked={includeOutdated}
                    disabled={isReadOnly || isRunning || preview.outdatedVersion === 0}
                    onCheckedChange={(v) => setIncludeOutdated(v === true)}
                    className="mt-0.5"
                  />
                  <span
                    className={preview.outdatedVersion === 0 ? 'text-muted-foreground' : undefined}
                  >
                    <span className="font-medium tabular-nums">
                      {preview.outdatedVersion.toLocaleString()}
                    </span>{' '}
                    thread{preview.outdatedVersion === 1 ? '' : 's'} analysed with an older version
                  </span>
                </label>

                <div className="border-t border-border/60 pt-3 flex flex-wrap justify-between gap-2">
                  <span className="font-medium tabular-nums">
                    Total: {selected.toLocaleString()} action{selected === 1 ? '' : 's'}
                  </span>
                  <span
                    className={
                      selected > remainingAllowance
                        ? 'text-destructive tabular-nums'
                        : 'text-muted-foreground tabular-nums'
                    }
                  >
                    {remainingAllowance.toLocaleString()} remaining in your plan
                  </span>
                </div>

                {selected > remainingAllowance && (
                  <p className="flex gap-2 text-destructive">
                    <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                    <span>
                      This is more than your remaining allowance. It will analyse as much as it can
                      and stop.
                    </span>
                  </p>
                )}
              </div>
            )}

            {progress && (
              <p className="text-sm text-muted-foreground tabular-nums">
                Analysed {progress.done.toLocaleString()} of {progress.total.toLocaleString()}…
              </p>
            )}

            <Button
              onClick={run}
              disabled={isReadOnly || isRunning || !rangeIsValid || selected === 0}
            >
              {isRunning
                ? 'Analysing…'
                : `Analyse ${selected.toLocaleString()} thread${selected === 1 ? '' : 's'}`}
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
